import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';

// Componente de efecto typewriter
const TypewriterText = ({ words, className = '' }: { words: string[]; className?: string }) => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const word = words[currentWordIndex];
    const typeSpeed = isDeleting ? 50 : 100;
    const pauseTime = 2000;

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (currentText.length < word.length) {
          setCurrentText(word.slice(0, currentText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), pauseTime);
        }
      } else {
        if (currentText.length > 0) {
          setCurrentText(word.slice(0, currentText.length - 1));
        } else {
          setIsDeleting(false);
          setCurrentWordIndex((prev) => (prev + 1) % words.length);
        }
      }
    }, typeSpeed);

    return () => clearTimeout(timeout);
  }, [currentText, isDeleting, currentWordIndex, words]);

  return (
    <span className={className}>
      {currentText}
      <span className="animate-pulse">|</span>
    </span>
  );
};

const HomePage = () => {
  const heroRef = useRef(null);
  const servicesRef = useRef(null);
  const benefitsRef = useRef(null);
  const processRef = useRef(null);
  const zonesRef = useRef(null);
  const faqRef = useRef(null);

  // Parallax effect for hero
  const { scrollY } = useScroll();
  const heroImageY = useTransform(scrollY, [0, 600], [0, 150]);
  const heroImageScale = useTransform(scrollY, [0, 600], [1, 1.15]);
  const heroOverlayOpacity = useTransform(scrollY, [0, 400], [0.3, 0.7]);

  const servicesInView = useInView(servicesRef, { once: true, amount: 0.2 });
  const benefitsInView = useInView(benefitsRef, { once: true, amount: 0.2 });
  const processInView = useInView(processRef, { once: true, amount: 0.2 });
  const zonesInView = useInView(zonesRef, { once: true, amount: 0.2 });
  const faqInView = useInView(faqRef, { once: true, amount: 0.2 });

  // Set document metadata
  useEffect(() => {
    document.title = 'Instalación de Pladur en Terrassa | Presupuesto Gratis 24h | PLADUREX';
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Instalación profesional de pladur en Terrassa. Tabiques, trasdosados, techos y aislamientos. +15 años de experiencia. Presupuesto gratis en 24h');
  }, []);

  const services = [
    {
      icon: 'ri-layout-grid-line',
      title: 'Tabiques de Pladur',
      items: ['Divisiones interiores', 'Distribución de espacios', 'Obra limpia y rápida', 'Acabado listo para pintar'],
      description: 'Creamos espacios funcionales con instalación rápida y limpia'
    },
    {
      icon: 'ri-building-line',
      title: 'Trasdosados',
      items: ['Directos y autoportantes', 'Con aislamiento térmico', 'Con aislamiento acústico', 'Antihumedad'],
      description: 'Mejoramos paredes existentes con aislamiento y acabado perfecto'
    },
    {
      icon: 'ri-stack-line',
      title: 'Falsos Techos',
      items: ['Continuos', 'Registrables', 'Desmontables', 'Con iluminación integrada'],
      description: 'Techos modernos que ocultan instalaciones y mejoran la estética'
    },
    {
      icon: 'ri-sound-module-line',
      title: 'Aislamientos',
      items: ['Acústico profesional', 'Térmico eficiente', 'Insonorización', 'Antihumedad'],
      description: 'Confort térmico y acústico para tu hogar u oficina'
    }
  ];

  const benefits = [
    {
      icon: 'ri-time-line',
      title: 'Obra Limpia y Rápida',
      description: 'Sistema de construcción en seco que reduce tiempos y suciedad en tu espacio'
    },
    {
      icon: 'ri-volume-mute-line',
      title: 'Mejor Aislamiento',
      description: 'Mejora significativa en aislamiento acústico y térmico de tu vivienda u oficina'
    },
    {
      icon: 'ri-pencil-ruler-2-line',
      title: 'Redistribución Flexible',
      description: 'Crea nuevos espacios sin obra húmeda, ideal para reformas y redistribuciones'
    }
  ];

  const process = [
    { number: 1, title: 'Contacto Inicial', description: 'Llámanos o escríbenos' },
    { number: 2, title: 'Visita y Medición', description: 'Evaluamos tu proyecto' },
    { number: 3, title: 'Presupuesto Detallado', description: 'Sin sorpresas' },
    { number: 4, title: 'Ejecución Profesional', description: 'Trabajo de calidad' },
    { number: 5, title: 'Entrega y Acabados', description: 'Listo para usar' }
  ];

  const zones = [
    'Centre', 'Ca n\'Aurell', 'Sant Pere', 'Can Boada', 'Can Palet',
    'Les Arenes', 'Vallparadís', 'La Maurina', 'Torre-sana', 'Ègara',
    'Sabadell', 'Matadepera', 'Viladecavalls', 'Rubí', 'Sant Cugat'
  ];

  const faqs = [
    {
      question: '¿Cuánto tiempo tarda una instalación de pladur en Terrassa?',
      answer: 'El tiempo depende del proyecto. Un tabique sencillo puede estar listo en 1-2 días, mientras que reformas completas pueden llevar 1-2 semanas. La ventaja del pladur es que es mucho más rápido que la obra tradicional.'
    },
    {
      question: '¿Qué factores afectan al precio de la instalación de pladur?',
      answer: 'Los principales factores son: metros cuadrados a instalar, altura de los techos, tipo de pladur (estándar, antihumedad, ignífugo), nivel de aislamiento requerido, complejidad del diseño y acabados especiales.'
    },
    {
      question: '¿El pladur aísla bien del ruido?',
      answer: 'Sí, especialmente cuando se instala con lana de roca o fibra de vidrio. Un trasdosado con aislamiento puede reducir significativamente el ruido entre estancias o del exterior.'
    },
    {
      question: '¿Se puede instalar pladur en baños y cocinas?',
      answer: 'Absolutamente. Utilizamos placas de pladur antihumedad (color verde) específicas para zonas húmedas como baños, cocinas y lavaderos. Son resistentes a la humedad y perfectas para alicatar encima.'
    }
  ];

  return (
    <div className="bg-deep-green min-h-screen overflow-x-hidden">
      <Header />
      <WhatsAppButton />
      <ScrollToTopButton />

      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(255,212,0,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,212,0,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-radial from-deep-green via-deep-green to-[#041A12]"></div>

        <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 py-12 sm:py-20 relative z-10">
          <div className="grid lg:grid-cols-[60%_40%] gap-8 sm:gap-12 items-center">
            {/* Left Column */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="w-full"
            >
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-text-light leading-tight mb-6">
                <span className="relative inline-block">
                  Instalación de Pladur
                  <motion.div
                    className="absolute -bottom-2 left-0 h-1 bg-primary-yellow"
                    initial={{ width: 0 }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 1, delay: 0.5 }}
                  ></motion.div>
                </span>
                <br />
                en <span className="text-primary-yellow">Terrassa</span>
              </h1>
              <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-4 max-w-full sm:max-w-[520px]">
                Acabados limpios, obra rápida y soluciones profesionales para tu hogar u oficina.
              </p>
              <p className="text-lg sm:text-xl text-primary-yellow font-semibold mb-8 h-8 overflow-hidden">
                <TypewriterText 
                  words={['Tabiques divisorios', 'Falsos techos', 'Trasdosados', 'Aislamiento acústico', 'Aislamiento térmico']} 
                />
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                <Link
                  to="/contacto"
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-primary-yellow text-deep-green font-bold rounded-lg hover:shadow-[0_0_30px_rgba(255,212,0,0.5)] hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap text-sm sm:text-base"
                >
                  Pedir Presupuesto Gratis
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/servicios"
                  className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-text-light text-text-light font-bold rounded-lg hover:bg-text-light hover:text-deep-green transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap text-sm sm:text-base"
                >
                  Ver Servicios
                  <i className="ri-service-line"></i>
                </Link>
              </div>
            </motion.div>

            {/* Right Column - Parallax Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative w-full"
            >
              <div className="relative rounded-2xl overflow-hidden w-full">
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-br from-card-green/60 to-transparent z-10"
                  style={{ opacity: heroOverlayOpacity }}
                ></motion.div>
                <motion.img
                  src="https://public.readdy.ai/ai/img_res/27358251fa1e47a0b56a0fc47725c9c2.jpg"
                  alt="Instalación profesional de pladur"
                  className="w-full h-[400px] sm:h-[500px] object-cover object-top"
                  style={{ 
                    y: heroImageY,
                    scale: heroImageScale
                  }}
                />
                <div className="absolute top-4 sm:top-6 right-4 sm:right-6 bg-primary-yellow text-deep-green px-3 sm:px-4 py-2 rounded-full font-bold text-xs sm:text-sm flex items-center gap-2 z-20">
                  <i className="ri-map-pin-line"></i>
                  <span className="whitespace-nowrap">Servicio en Terrassa</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-12 sm:py-20 relative overflow-hidden">
        <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-text-light mb-4">
              Nuestros servicios
            </h2>
            <div className="w-16 h-1 bg-primary-yellow mx-auto mb-4"></div>
            <p className="text-gray-300 text-base sm:text-lg max-w-full sm:max-w-[600px] mx-auto px-4">
              <a 
                href="https://badalona.pladures.es/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-primary-yellow transition-colors"
              >
                Soluciones completas de pladur para todo tipo de proyectos en Terrassa
              </a> y alrededores
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
            {services.map((service, index) => (
              <Link
                key={index}
                to="/servicios"
                className="block"
              >
                <motion.div
                  initial={{ opacity: 0, y: 60, scale: 0.9, rotateX: 15 }}
                  animate={servicesInView ? { 
                    opacity: 1, 
                    y: 0, 
                    scale: 1, 
                    rotateX: 0 
                  } : {}}
                  transition={{ 
                    duration: 0.7, 
                    delay: index * 0.15,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                  whileHover={{ 
                    y: -8, 
                    scale: 1.02,
                    transition: { duration: 0.3 }
                  }}
                  className="bg-card-green rounded-xl p-6 sm:p-8 group relative overflow-hidden cursor-pointer w-full h-full"
                  style={{ transformPerspective: 1000 }}
                >
                  {/* Animated background gradient on hover */}
                  <motion.div 
                    className="absolute inset-0 bg-gradient-to-br from-primary-yellow/5 to-accent-lime/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  />
                  
                  {/* Top line animation */}
                  <motion.div 
                    className="absolute top-0 left-0 h-1 bg-primary-yellow"
                    initial={{ width: 0 }}
                    animate={servicesInView ? { width: '100%' } : { width: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.15 + 0.5 }}
                  />
                  
                  {/* Corner accent */}
                  <motion.div
                    className="absolute -top-10 -right-10 w-20 h-20 bg-primary-yellow/10 rounded-full"
                    initial={{ scale: 0 }}
                    animate={servicesInView ? { scale: 1 } : { scale: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.15 + 0.3 }}
                  />
                  
                  <div className="flex items-start gap-3 sm:gap-4 mb-4 relative z-10">
                    <motion.div 
                      className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-accent-lime/20 flex items-center justify-center flex-shrink-0"
                      initial={{ scale: 0, rotate: -180 }}
                      animate={servicesInView ? { scale: 1, rotate: 0 } : {}}
                      transition={{ 
                        duration: 0.6, 
                        delay: index * 0.15 + 0.2,
                        type: "spring",
                        stiffness: 200
                      }}
                    >
                      <i className={`${service.icon} text-primary-yellow text-xl sm:text-2xl`}></i>
                    </motion.div>
                    <div className="flex-1 min-w-0">
                      <motion.h3 
                        className="text-xl sm:text-2xl font-bold text-text-light mb-2 break-words"
                        initial={{ opacity: 0, x: -20 }}
                        animate={servicesInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.5, delay: index * 0.15 + 0.3 }}
                      >
                        {service.title}
                      </motion.h3>
                      <motion.p 
                        className="text-xs sm:text-sm text-gray-400 break-words"
                        initial={{ opacity: 0 }}
                        animate={servicesInView ? { opacity: 1 } : {}}
                        transition={{ duration: 0.5, delay: index * 0.15 + 0.4 }}
                      >
                        {service.description}
                      </motion.p>
                    </div>
                  </div>

                  <ul className="space-y-2 mb-4 relative z-10">
                    {service.items.map((item, idx) => (
                      <motion.li 
                        key={idx} 
                        className="flex items-center gap-2 text-gray-300"
                        initial={{ opacity: 0, x: -30 }}
                        animate={servicesInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ 
                          duration: 0.4, 
                          delay: index * 0.15 + 0.4 + idx * 0.08 
                        }}
                      >
                        <motion.i 
                          className="ri-checkbox-circle-fill text-accent-lime flex-shrink-0"
                          initial={{ scale: 0 }}
                          animate={servicesInView ? { scale: 1 } : {}}
                          transition={{ 
                            duration: 0.3, 
                            delay: index * 0.15 + 0.5 + idx * 0.08,
                            type: "spring",
                            stiffness: 300
                          }}
                        />
                        <span className="text-xs sm:text-sm break-words">{item}</span>
                      </motion.li>
                    ))}
                  </ul>
                  
                  {/* Ver más indicator */}
                  <div className="flex items-center gap-2 text-primary-yellow text-sm font-semibold relative z-10 group-hover:translate-x-2 transition-transform duration-300">
                    <span>Ver detalles del servicio</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
          
          {/* Link to all services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="text-center mt-10"
          >
            <Link
              to="/servicios"
              className="inline-flex items-center gap-2 text-primary-yellow hover:text-accent-lime transition-colors font-semibold"
            >
              Ver todos nuestros servicios de pladur
              <i className="ri-arrow-right-line"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={benefitsRef} className="py-20 bg-card-green/30">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-card-green to-accent-lime/30 flex items-center justify-center mx-auto mb-6">
                  <i className={`${benefit.icon} text-text-light text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-text-light mb-3">{benefit.title}</h3>
                <p className="text-gray-300 leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
          
          {/* Link to empresa */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center mt-10"
          >
            <Link
              to="/empresa"
              className="inline-flex items-center gap-2 text-primary-yellow hover:text-accent-lime transition-colors font-semibold"
            >
              Conoce más sobre nuestra empresa y metodología
              <i className="ri-arrow-right-line"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <StatsSection />

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* Process Section */}
      <section ref={processRef} className="py-20">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={processInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-text-light mb-4">
              Cómo trabajamos
            </h2>
            <div className="w-16 h-1 bg-primary-yellow mx-auto"></div>
          </motion.div>

          <div className="hidden md:flex items-center justify-between relative max-w-[1000px] mx-auto">
            {/* Connecting Line */}
            <div className="absolute top-[30px] left-0 right-0 h-1 bg-primary-yellow/30 z-0"></div>
            <motion.div
              className="absolute top-[30px] left-0 h-1 bg-primary-yellow z-0"
              initial={{ width: 0 }}
              animate={processInView ? { width: '100%' } : {}}
              transition={{ duration: 2, delay: 0.5 }}
            ></motion.div>

            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={processInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="flex flex-col items-center relative z-10"
              >
                <div className="w-16 h-16 rounded-full bg-primary-yellow flex items-center justify-center text-deep-green font-bold text-2xl mb-4">
                  {step.number}
                </div>
                <h3 className="font-bold text-text-light text-center mb-1 text-sm max-w-[120px]">
                  {step.title}
                </h3>
                <p className="text-gray-400 text-xs text-center max-w-[120px]">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Mobile Version */}
          <div className="md:hidden space-y-6">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                animate={processInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-start gap-4"
              >
                <div className="w-12 h-12 rounded-full bg-primary-yellow flex items-center justify-center text-deep-green font-bold text-xl flex-shrink-0">
                  {step.number}
                </div>
                <div>
                  <h3 className="font-bold text-text-light mb-1">{step.title}</h3>
                  <p className="text-gray-400 text-sm">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* Link to empresa for more details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={processInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 1.2 }}
            className="text-center mt-12"
          >
            <Link
              to="/empresa"
              className="inline-flex items-center gap-2 text-primary-yellow hover:text-accent-lime transition-colors font-semibold"
            >
              Descubre más sobre nuestro proceso de trabajo
              <i className="ri-arrow-right-line"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Zones Section */}
      <section ref={zonesRef} className="py-12 sm:py-20 bg-card-green/30 overflow-hidden">
        <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={zonesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 sm:mb-12"
          >
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-text-light mb-4">
              Zonas de servicio
            </h2>
            <div className="w-16 h-1 bg-primary-yellow mx-auto mb-4"></div>
            <p className="text-gray-300 text-base sm:text-lg max-w-full sm:max-w-[600px] mx-auto px-4">
              Ofrecemos <a 
                href="https://pladures.es/barcelona/terrassa/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-primary-yellow transition-colors"
              >
                servicios de instalación de pladur en Terrassa
              </a> y toda la comarca del Vallès Occidental
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-start">
            {/* Map Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={zonesInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="relative w-full"
            >
              <div className="relative rounded-2xl overflow-hidden w-full">
                <img
                  src="https://readdy.ai/api/search-image?query=illustrated%20map%20of%20Terrassa%20region%20with%20location%20markers%20modern%20design&width=600&height=500&seq=map-terrassa-zones-002&orientation=landscape"
                  alt="Mapa de zonas de servicio en Terrassa y Vallès"
                  className="w-full h-[300px] sm:h-[420px] object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-deep-green/60 to-transparent"></div>
                <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6">
                  <div className="bg-card-green/90 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-accent-lime/30">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary-yellow/20 flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-deep-green text-lg sm:text-xl"></i>
                      </div>
                      <div className="min-w-0">
                        <p className="text-text-light font-bold text-sm sm:text-base break-words">Servicio en toda la comarca</p>
                        <p className="text-gray-400 text-xs sm:text-sm break-words">Terrassa, Vallès Occidental y Barcelona</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Zones Lists */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={zonesInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6 sm:space-y-8 w-full"
            >
              {/* Barrios de Terrassa */}
              <div>
                <div className="flex items-center gap-2 sm:gap-3 mb-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary-yellow/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-2-line text-primary-yellow text-base sm:text-lg"></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-text-light">Barrios de Terrassa</h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                  {[
                    'Centre', 'Ca n\'Aurell', 'Sant Pere', 'Can Boada', 'Can Palet',
                    'Les Arenes', 'Vallparadís', 'La Maurina', 'Torre-sana', 'Ègara',
                    'Can Roca', 'Les Fonts'
                  ].map((zone, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={zonesInView ? { opacity: 1, scale: 1 } : {}}
                      transition={{ duration: 0.3, delay: 0.3 + index * 0.04 }}
                      className="bg-card-green border border-accent-lime/20 px-2 sm:px-4 py-2 sm:py-2.5 rounded-lg text-center text-xs sm:text-sm text-gray-300 hover:bg-accent-lime hover:text-deep-green hover:border-accent-lime transition-all duration-300 cursor-default flex items-center justify-center gap-1 sm:gap-2"
                    >
                      <i className="ri-map-pin-2-line text-accent-lime text-xs flex-shrink-0"></i>
                      <span className="whitespace-nowrap overflow-hidden text-ellipsis">{zone}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Municipios Cercanos */}
              <div>
                <div className="flex items-center gap-2 sm:gap-3 mb-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-accent-lime/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-road-map-line text-accent-lime text-base sm:text-lg"></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-text-light">Municipios Cercanos</h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                  {[
                    'Sabadell', 'Rubí', 'Sant Cugat', 'Matadepera', 'Viladecavalls',
                    'Castellbisbal', 'Ullastrell', 'Vacarisses', 'Rellinars', 'Sant Quirze',
                    'Cerdanyola', 'Barberà del Vallès'
                  ].map((zone, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={zonesInView ? { opacity: 1, scale: 1 } : {}}
                      transition={{ duration: 0.3, delay: 0.5 + index * 0.04 }}
                      className="bg-card-green border border-primary-yellow/20 px-2 sm:px-4 py-2 sm:py-2.5 rounded-lg text-center text-xs sm:text-sm text-gray-300 hover:bg-primary-yellow hover:text-deep-green hover:border-primary-yellow transition-all duration-300 cursor-default flex items-center justify-center gap-1 sm:gap-2"
                    >
                      <i className="ri-car-line text-primary-yellow text-xs flex-shrink-0"></i>
                      <span className="whitespace-nowrap overflow-hidden text-ellipsis">{zone}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={zonesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.8 }}
                className="bg-gradient-to-r from-primary-yellow/10 to-accent-lime/10 rounded-xl p-4 sm:p-5 border border-primary-yellow/30"
              >
                <p className="text-gray-300 text-xs sm:text-sm mb-3">
                  ¿No encuentras tu zona? Consúltanos, probablemente también llegamos.
                </p>
                <Link
                  to="/contacto"
                  className="inline-flex items-center gap-2 text-primary-yellow hover:text-accent-lime transition-colors font-semibold text-xs sm:text-sm"
                >
                  Consultar disponibilidad
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mini FAQ Section */}
      <section ref={faqRef} className="py-12 sm:py-20 overflow-hidden">
        <div className="w-full max-w-[900px] mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={faqInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-text-light mb-4">
              Preguntas frecuentes
            </h2>
            <div className="w-16 h-1 bg-primary-yellow mx-auto mb-4"></div>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem key={index} faq={faq} index={index} inView={faqInView} />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={faqInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-8"
          >
            <Link
              to="/faq"
              className="text-primary-yellow hover:text-accent-lime transition-colors font-semibold inline-flex items-center gap-2"
            >
              Ver todas las preguntas
              <i className="ri-arrow-right-line"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-12 sm:py-20 bg-gradient-to-b from-card-green to-black overflow-hidden">
        <div className="w-full max-w-[900px] mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-text-light mb-4">
              ¿Listo para tu proyecto de pladur?
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 mb-8">
              Presupuesto sin compromiso en 24 horas
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4">
              <a
                href="tel:+34629501941"
                className="px-8 sm:px-10 py-4 sm:py-5 bg-primary-yellow text-deep-green font-bold text-base sm:text-lg rounded-lg hover:shadow-[0_0_40px_rgba(255,212,0,0.6)] hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap"
              >
                <i className="ri-phone-line text-lg sm:text-xl"></i>
                Llamar Ahora
              </a>
              <a
                href="https://wa.me/34629501941"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 sm:px-10 py-4 sm:py-5 border-2 border-primary-yellow text-primary-yellow font-bold text-base sm:text-lg rounded-lg hover:bg-primary-yellow hover:text-deep-green transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg sm:text-xl"></i>
                Enviar WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const FAQItem = ({ faq, index, inView }: { faq: { question: string; answer: string }; index: number; inView: boolean }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="bg-card-green rounded-lg overflow-hidden hover:border-l-4 hover:border-primary-yellow transition-all duration-300"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer"
      >
        <h4 className="text-lg font-bold text-text-light pr-4">{faq.question}</h4>
        <i className={`ri-add-line text-primary-yellow text-2xl transition-transform duration-300 flex-shrink-0 ${isOpen ? 'rotate-45' : ''}`}></i>
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <div className="px-6 pb-5 pt-0">
          <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Componente de contador animado
const AnimatedCounter = ({ 
  end, 
  duration = 2000, 
  suffix = '', 
  prefix = '',
  inView 
}: { 
  end: number; 
  duration?: number; 
  suffix?: string; 
  prefix?: string;
  inView: boolean;
}) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(0);
  const startTimeRef = useRef<number | null>(null);

  const animate = useCallback((timestamp: number) => {
    if (!startTimeRef.current) {
      startTimeRef.current = timestamp;
    }
    
    const progress = timestamp - startTimeRef.current;
    const percentage = Math.min(progress / duration, 1);
    
    // Easing function for smooth animation
    const easeOutQuart = 1 - Math.pow(1 - percentage, 4);
    const currentCount = Math.floor(easeOutQuart * end);
    
    if (currentCount !== countRef.current) {
      countRef.current = currentCount;
      setCount(currentCount);
    }
    
    if (percentage < 1) {
      requestAnimationFrame(animate);
    } else {
      setCount(end);
    }
  }, [end, duration]);

  useEffect(() => {
    if (inView) {
      startTimeRef.current = null;
      countRef.current = 0;
      setCount(0);
      requestAnimationFrame(animate);
    }
  }, [inView, animate]);

  return (
    <span className="tabular-nums">
      {prefix}{count.toLocaleString('es-ES')}{suffix}
    </span>
  );
};

// Sección de estadísticas
const StatsSection = () => {
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true, amount: 0.3 });

  const stats = [
    {
      value: 15,
      suffix: '+',
      label: 'Años de experiencia',
      icon: 'ri-calendar-check-line',
      description: 'en instalación de pladur'
    },
    {
      value: 500,
      suffix: '+',
      label: 'Proyectos completados',
      icon: 'ri-building-2-line',
      description: 'en Terrassa y alrededores'
    },
    {
      value: 98,
      suffix: '%',
      label: 'Clientes satisfechos',
      icon: 'ri-emotion-happy-line',
      description: 'nos recomiendan'
    },
    {
      value: 24,
      suffix: 'h',
      label: 'Presupuesto gratis',
      icon: 'ri-time-line',
      description: 'respuesta garantizada'
    }
  ];

  return (
    <section ref={statsRef} className="py-20 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,212,0,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(163,230,53,0.3) 0%, transparent 50%)'
        }}></div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={statsInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-text-light mb-4">
            Cifras que nos avalan
          </h2>
          <div className="w-16 h-1 bg-primary-yellow mx-auto mb-4"></div>
          <p className="text-gray-300 text-lg max-w-[600px] mx-auto">
            Nuestra trayectoria habla por sí sola
          </p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40, scale: 0.9 }}
              animate={statsInView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.15,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              className="relative group"
            >
              <div className="bg-card-green rounded-xl p-6 lg:p-8 text-center relative overflow-hidden hover:scale-105 transition-transform duration-300">
                {/* Glow effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary-yellow/0 to-accent-lime/0 group-hover:from-primary-yellow/10 group-hover:to-accent-lime/10 transition-all duration-500"></div>
                
                {/* Icon */}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={statsInView ? { scale: 1, rotate: 0 } : {}}
                  transition={{ 
                    duration: 0.6, 
                    delay: index * 0.15 + 0.2,
                    type: "spring",
                    stiffness: 200
                  }}
                  className="w-14 h-14 lg:w-16 lg:h-16 rounded-full bg-gradient-to-br from-primary-yellow/20 to-accent-lime/20 flex items-center justify-center mx-auto mb-4"
                >
                  <i className={`${stat.icon} text-primary-yellow text-2xl lg:text-3xl`}></i>
                </motion.div>

                {/* Counter */}
                <div className="text-4xl lg:text-5xl font-extrabold text-primary-yellow mb-2">
                  <AnimatedCounter 
                    end={stat.value} 
                    suffix={stat.suffix}
                    duration={2000 + index * 200}
                    inView={statsInView}
                  />
                </div>

                {/* Label */}
                <h3 className="text-lg lg:text-xl font-bold text-text-light mb-1">
                  {stat.label}
                </h3>
                <p className="text-sm text-gray-400">
                  {stat.description}
                </p>

                {/* Bottom accent line */}
                <motion.div
                  className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-primary-yellow to-accent-lime"
                  initial={{ width: 0 }}
                  animate={statsInView ? { width: '100%' } : { width: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.15 + 0.5 }}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const testimonialsRef = useRef(null);
  const testimonialsInView = useInView(testimonialsRef, { once: true, amount: 0.2 });

  const testimonials = [
    {
      name: 'María García',
      location: 'Centre, Terrassa',
      text: 'Excelente trabajo en la instalación de tabiques para dividir el salón. Obra limpia, rápida y el acabado quedó perfecto. Muy profesionales y puntuales.',
      project: 'Tabiques divisorios',
      rating: 5
    },
    {
      name: 'Carlos Martínez',
      location: 'Ca n\'Aurell, Terrassa',
      text: 'Contratamos el aislamiento acústico para el dormitorio y la diferencia es increíble. Ya no escuchamos el ruido de la calle. Muy recomendables.',
      project: 'Aislamiento acústico',
      rating: 5
    },
    {
      name: 'Laura Sánchez',
      location: 'Vallparadís, Terrassa',
      text: 'Instalaron el falso techo en toda la laicina con iluminación integrada. El resultado es espectacular y cumplieron con los plazos acordados.',
      project: 'Falso techo con iluminación',
      rating: 5
    },
    {
      name: 'Jordi Puig',
      location: 'Sant Pere, Terrassa',
      text: 'Reforma completa del baño con pladur antihumedad. Trabajo impecable, muy limpios durante toda la obra y el precio muy competitivo.',
      project: 'Pladur antihumedad baño',
      rating: 5
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <section ref={testimonialsRef} className="py-20">
      <div className="max-w-[1200px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-text-light mb-4">
            Clientes satisfechos
          </h2>
          <div className="w-16 h-1 bg-primary-yellow mx-auto mb-4"></div>
          <p className="text-gray-300 text-lg max-w-[600px] mx-auto">
            Lo que dicen nuestros clientes sobre nuestro trabajo en Terrassa
          </p>
        </motion.div>

        <div className="relative overflow-hidden">
          <motion.div
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {testimonials.map((testimonial, index) => (
              <div key={index} className="w-full flex-shrink-0 px-4">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={testimonialsInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="bg-card-green rounded-xl p-8 md:p-10 max-w-[800px] mx-auto relative"
                >
                  <div className="absolute top-6 left-8 text-primary-yellow/20 text-6xl font-serif">"</div>
                  
                  <div className="relative z-10">
                    <div className="flex items-center gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-primary-yellow text-lg"></i>
                      ))}
                    </div>
                    
                    <p className="text-gray-300 text-lg leading-relaxed mb-6 italic">
                      "{testimonial.text}"
                    </p>
                    
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div>
                        <h4 className="text-text-light font-bold text-lg">{testimonial.name}</h4>
                        <p className="text-gray-400 text-sm flex items-center gap-1">
                          <i className="ri-map-pin-line text-accent-lime"></i>
                          {testimonial.location}
                        </p>
                      </div>
                      <div className="bg-accent-lime/20 px-4 py-2 rounded-full">
                        <span className="text-accent-lime text-sm font-medium">{testimonial.project}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            ))}
          </motion.div>

          {/* Navigation Dots */}
          <div className="flex justify-center gap-3 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                  currentIndex === index
                    ? 'bg-primary-yellow w-8'
                    : 'bg-gray-600 hover:bg-gray-500'
                }`}
                aria-label={`Ir al testimonio ${index + 1}`}
              />
            ))}
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={() => setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
            className="absolute left-0 top-1/2 -translate-y-1/2 w-12 h-12 bg-card-green/80 rounded-full flex items-center justify-center text-text-light hover:bg-primary-yellow hover:text-deep-green transition-all duration-300 cursor-pointer hidden md:flex"
            aria-label="Testimonio anterior"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>
          <button
            onClick={() => setCurrentIndex((prev) => (prev + 1) % testimonials.length)}
            className="absolute right-0 top-1/2 -translate-y-1/2 w-12 h-12 bg-card-green/80 rounded-full flex items-center justify-center text-text-light hover:bg-primary-yellow hover:text-deep-green transition-all duration-300 cursor-pointer hidden md:flex"
            aria-label="Siguiente testimonio"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>
        </div>
      </div>
    </section>
  );
};

export default HomePage;
